import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ApiError,
  alPerderLaSesion,
  api,
  avisarQueSeVa,
  setCsrfToken,
  type SessionResponse,
} from '../api/client';
import i18n from '../i18n';
import { recordarIdioma } from '../i18n/idioma';
import { veniaDeUnAcceso } from './regreso';
import { AvisoDeSesion } from './AvisoDeSesion';
import { proximaRevision, vigenciaDe, type Vigencia } from './inactividad';
import {
  LATIDO_DE_PESTANA_MS,
  despedirse,
  esSalidaAProposito,
  idDeEstaPagina,
  latir,
  renovarIdDeLaPagina,
  saldraAProposito,
} from './pestanas';

type Estado =
  | { status: 'cargando' }
  | { status: 'anonimo' }
  | { status: 'autenticado'; session: SessionResponse };

interface SessionContextValue {
  estado: Estado;
  /** Los permisos ya vienen resueltos por rol y ámbito desde la API. */
  puede: (permission: string) => boolean;
  recargar: () => Promise<void>;
  salir: () => Promise<void>;
  /**
   * El acceso funcionó y aun así no hay sesión: la cookie no sobrevivió el
   * viaje de vuelta. Es un callejón sin salida — repetir el acceso da lo mismo —
   * y por eso se nombra en vez de dejar la portada como si nada hubiera pasado.
   */
  sesionNoLlego: boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ status: 'cargando' });
  // La marca ya se recogió al arrancar (`recogerMarcaDeAcceso`); aquí sólo se
  // consulta lo que dijo, porque de la barra de direcciones ya no está.
  const [regreso] = useState(() => veniaDeUnAcceso());

  const recargar = useCallback(async () => {
    try {
      const session = await api.session();
      setCsrfToken(session.csrfToken);
      if (session.locale && session.locale !== i18n.language) {
        await i18n.changeLanguage(session.locale);
      }
      // Para que la próxima pantalla de acceso, que sale antes de saber quién
      // es, ya salga en su idioma.
      if (session.locale) recordarIdioma(session.locale);
      setEstado({ status: 'autenticado', session });
    } catch (error) {
      // Un 401 no es un fallo: es no haber entrado todavía.
      if (error instanceof ApiError && error.status === 401) {
        setCsrfToken(null);
        setEstado({ status: 'anonimo' });
        return;
      }
      throw error;
    }
  }, []);

  // Salida completa, igual que por inactividad: recarga desde la portada y no
  // queda en memoria nada de lo que había en pantalla (ADR-0021). La API ya cerró
  // la sesión del portal y la de Keycloak; si la petición falló, la recarga
  // muestra lo que de verdad hay.
  const salir = useCallback(async () => {
    saldraAProposito(Date.now());
    try {
      await api.logout();
    } catch {
      // Ver arriba.
    }
    window.location.replace('/?salida=voluntaria');
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  // Cerrar la última pestaña cierra la sesión (ADR-0030). Cada pestaña se anota
  // y late; al irse se borra y, si no queda otra, avisa. El servidor espera un
  // momento: si la página vuelve —se recargó—, no pasa nada.
  useEffect(() => {
    if (estado.status !== 'autenticado') return;
    const pestanas = window.localStorage;
    const anotarse = () => latir(pestanas, idDeEstaPagina(), Date.now());
    anotarse();
    const latido = window.setInterval(anotarse, LATIDO_DE_PESTANA_MS);

    const alIrse = () => {
      const quedaOtra = despedirse(pestanas, idDeEstaPagina(), Date.now());
      if (!quedaOtra && !esSalidaAProposito(Date.now())) avisarQueSeVa();
    };
    // Volver de la caché del navegador es otra carga: se anuncia como tal, o el
    // servidor la tomaría por la página que se fue.
    const alVolverDeLaCache = (evento: PageTransitionEvent) => {
      if (!evento.persisted) return;
      renovarIdDeLaPagina();
      anotarse();
      void api.sessionStatus().catch(() => undefined);
    };
    // Ir a cambiar la contraseña o al código en Keycloak es salir a propósito.
    const alHacerClic = (evento: MouseEvent) => {
      const destino = evento.target instanceof Element ? evento.target.closest('a[href^="/api/v1/auth/"]') : null;
      if (destino) saldraAProposito(Date.now());
    };

    window.addEventListener('pagehide', alIrse);
    window.addEventListener('pageshow', alVolverDeLaCache);
    document.addEventListener('click', alHacerClic, true);
    return () => {
      window.clearInterval(latido);
      window.removeEventListener('pagehide', alIrse);
      window.removeEventListener('pageshow', alVolverDeLaCache);
      document.removeEventListener('click', alHacerClic, true);
    };
  }, [estado.status]);

  // Con la sesión abierta, la aplicación pregunta si sigue viva —sin alargarla—
  // cada minuto, al llegar a los dos minutos finales y al despertar la
  // computadora. En esos dos minutos avisa y ofrece «Sigo aquí». Cuando la
  // inactividad la vence, sale por completo: recarga desde la portada, sin nada
  // de lo que había en pantalla, y para volver se pide usuario y contraseña desde
  // cero (ADR-0021).
  const autenticado = estado.status === 'autenticado';
  const [vigencia, setVigencia] = useState<Vigencia | null>(null);
  const revisarAhora = useRef<() => Promise<void>>(async () => undefined);
  useEffect(() => {
    if (!autenticado) return;
    alPerderLaSesion((codigo) => {
      const motivo =
        codigo === 'auth.second_factor_required'
          ? 'segundo-factor'
          : codigo === 'auth.session_replaced'
            ? 'otro-dispositivo'
            : 'inactividad';
      window.location.replace(`/?salida=${motivo}`);
    });
    let vigente = true;
    let siguiente: number | undefined;
    const revisar = async () => {
      let espera = 60_000;
      try {
        const nueva = vigenciaDe(await api.sessionStatus(), Date.now());
        if (!vigente) return;
        setVigencia(nueva);
        espera = proximaRevision(nueva.plazo - Date.now());
      } catch {
        // Un 401 ya sacó a la persona (`alPerderLaSesion`). Otra falla —la red, la
        // API reiniciándose— no cierra nada: se vuelve a preguntar.
      }
      if (!vigente) return;
      // Puede haber dos revisiones en vuelo (la programada y la de volver a la
      // pestaña): la última en terminar programa la siguiente, no las dos.
      window.clearTimeout(siguiente);
      siguiente = window.setTimeout(() => void revisar(), espera);
    };
    revisarAhora.current = revisar;
    void revisar();
    const alVolver = () => {
      if (document.visibilityState === 'visible') void revisar();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      vigente = false;
      alPerderLaSesion(null);
      window.clearTimeout(siguiente);
      document.removeEventListener('visibilitychange', alVolver);
      revisarAhora.current = async () => undefined;
    };
  }, [autenticado]);

  const seguir = useCallback(async () => {
    try {
      await api.extendSession();
    } catch {
      // Si ya había vencido, el 401 la sacó; si fue la red, el aviso sigue ahí.
      return;
    }
    await revisarAhora.current();
  }, []);

  const valor = useMemo<SessionContextValue>(
    () => ({
      estado,
      puede: (permission: string) =>
        estado.status === 'autenticado' && estado.session.permissions.includes(permission),
      recargar,
      salir,
      sesionNoLlego: estado.status === 'anonimo' && regreso,
    }),
    [estado, recargar, salir, regreso],
  );

  return (
    <SessionContext.Provider value={valor}>
      {children}
      {autenticado && vigencia && (
        <AvisoDeSesion vigencia={vigencia} onSeguir={seguir} onSalir={() => void salir()} />
      )}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const contexto = useContext(SessionContext);
  if (!contexto) throw new Error('useSession must be used inside SessionProvider');
  return contexto;
}
